<script setup lang="ts">
import { ANCHOR_OFFSET_PX, PREVIEW_CARD_ID, VIEWPORT_MARGIN_PX } from '@/composables/useCalendarEventPreview';
import type { CalendarPreviewModel } from '@/composables/useCalendarEventPreview';
import { ReservationStatus } from '@/lib/types/reservation';
import { isForeignTimezoneAt, msToResourceLocalCalendarDate, msToResourceLocalTimeString } from '@/utils/date';
import { getReservationStatusLabel } from '@/plugins/i18n/enums';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Called once at setup scope on purpose: ReservationDetail.vue calls useI18n() inside a formatter body, which is a defect.
const { t, locale } = useI18n();

const props = defineProps<{
    open: boolean;
    model: CalendarPreviewModel | null;
    anchorRect: DOMRect | null;
    touchMode: boolean;
}>();

const emit = defineEmits<{
    close: [];
    cardEnter: [];
    cardLeave: [];
}>();

const cardRef = ref<HTMLElement | null>(null);
const top = ref(0);
const left = ref(0);

const isVisible = computed(() => props.open && props.model !== null && props.anchorRect !== null);
const isMaintenance = computed(() => props.model?.eventType === 'maintenance');

// Mirrors ReservationDetail.vue:491-498 so a preview and the modal it precedes never disagree — except for the
// severity token itself. PrimeVue 4.3.2 accepts 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast'
// (primevue/tag/index.d.ts:85, and the style module branches on 'warn'). 'warning' matches no branch, so a Planned
// reservation fell through to the default styling instead of amber. The union below is hand-written, so
// TypeScript could not catch it. ReservationDetail.vue:493 still carries the same defect and is outside this
// phase's scope; fixing it there is the moment to hoist this map into one shared module.
const STATUS_SEVERITY: Record<ReservationStatus, 'success' | 'info' | 'warn' | 'danger'> = {
    [ReservationStatus.Planned]: 'warn',
    [ReservationStatus.Canceled]: 'danger',
    [ReservationStatus.Completed]: 'info'
};

/**
 * Pins the card to the anchor and flips rather than clips (D-01).
 * Runs only once the card is in the DOM, because it reads its own offsetWidth/offsetHeight.
 */
function updatePlacement() {
    const rect = props.anchorRect;
    const el = cardRef.value;
    if (rect === null || el === null) {
        return;
    }

    const cardWidth = el.offsetWidth;
    const cardHeight = el.offsetHeight;

    // Default: right of the anchor, top-aligned.
    let nextLeft = rect.right + ANCHOR_OFFSET_PX;
    let nextTop = rect.top;

    // Horizontal flip: not enough room on the right, place it on the left instead.
    if (rect.right + ANCHOR_OFFSET_PX + cardWidth > window.innerWidth - VIEWPORT_MARGIN_PX) {
        nextLeft = rect.left - ANCHOR_OFFSET_PX - cardWidth;
    }

    // Vertical flip: bottom-align to the anchor rather than overflow the viewport bottom.
    if (rect.top + cardHeight > window.innerHeight - VIEWPORT_MARGIN_PX) {
        nextTop = rect.bottom - cardHeight;
    }

    // Final clamp after both flips — this is what guarantees no clipping, including inside the "+N more" popover.
    // Both axes are clamped on both edges. The vertical pair matters because getBoundingClientRect() reports an
    // event element's full untruncated box even when the timeGrid scroller visually clips it, so `rect.bottom`
    // (and therefore the bottom-align flip above) can land past the viewport bottom on a tall part-visible event.
    // Math.min runs before Math.max so an overlay taller than the viewport pins to the top margin, not off-screen.
    nextLeft = Math.min(nextLeft, window.innerWidth - cardWidth - VIEWPORT_MARGIN_PX);
    nextLeft = Math.max(nextLeft, VIEWPORT_MARGIN_PX);
    nextTop = Math.min(nextTop, window.innerHeight - cardHeight - VIEWPORT_MARGIN_PX);
    nextTop = Math.max(nextTop, VIEWPORT_MARGIN_PX);

    left.value = nextLeft;
    top.value = nextTop;
}

watch(
    () => [props.open, props.model, props.anchorRect],
    async () => {
        if (!isVisible.value) {
            return;
        }
        await nextTick();
        updatePlacement();
    },
    { immediate: true }
);

const cardStyle = computed(() => {
    const style: Record<string, string> = {
        top: `${top.value}px`,
        left: `${left.value}px`
    };

    // A maintenance takes its accent from the --maintenance modifier instead: an inline custom property outranks
    // every stylesheet rule, so setting it here would defeat the scheme-aware dark-mode amber.
    if (props.model !== null && props.model.eventType !== 'maintenance') {
        style['--preview-accent'] = props.model.accentColor;
    }

    return style;
});

const displayTitle = computed(() => {
    if (props.model === null) {
        return '';
    }
    const title = (props.model.title ?? '').trim();
    if (title === '') {
        // Gated on the branch: an untitled reservation labelled "Manutenzione" would read to an operator as a
        // resource maintenance, which is the semantic opposite of a customer visit in this domain.
        return isMaintenance.value ? t('calendar.preview.maintenance') : t('calendar.preview.untitled');
    }
    return title;
});

const excerptLabel = computed(() => (isMaintenance.value ? t('calendar.preview.description') : t('calendar.preview.notes')));

function pad(value: number): string {
    return String(value).padStart(2, '0');
}

/**
 * THE PRIMARY RANGE: `HH:mm – HH:mm · dd MMM` on the SPACE's clock, the same clock the grid now draws the event on.
 * The card owns its own format. `model.startMs` / `endMs` are TRUE instants on both event branches, so the conversion
 * here is still required and is NOT the grid's display shift read back.
 */
const resourceRangeText = computed(() => {
    if (props.model === null) {
        return '';
    }
    const timeZoneId = props.model.resourceTimezoneId;
    const startTime = msToResourceLocalTimeString(timeZoneId, props.model.startMs);
    const endTime = msToResourceLocalTimeString(timeZoneId, props.model.endMs);
    const dayDate = msToResourceLocalCalendarDate(timeZoneId, props.model.startMs) ?? new Date(props.model.startMs);
    const dayLabel = dayDate.toLocaleDateString(locale.value, { day: '2-digit', month: 'short' });
    return `${startTime} – ${endTime} · ${dayLabel}`;
});

/**
 * D-16/D-17/IC-1: whether this event's resource sits on a different UTC offset than the operator's browser at the
 * event's own START instant. One predicate, one implementation, shared with the reservation detail modal — the two
 * surfaces can never disagree about whether a clock is foreign.
 *
 * It returns false for an unresolvable zone, and that false is what makes the degraded card show ONE range row rather
 * than two identical ones.
 */
const showBrowserLocalRange = computed(() => props.model !== null && isForeignTimezoneAt(props.model.resourceTimezoneId, props.model.startMs));

/**
 * THE SECONDARY RANGE: the operator's own browser clock, demoted to the globe-marked row beneath the primary.
 * This body is the card's original `rangeText` moved verbatim — it is the reference rendering the same-zone case is
 * compared against, so it must keep producing today's exact string. Do not "improve" it.
 */
const browserRangeText = computed(() => {
    if (props.model === null || !showBrowserLocalRange.value) {
        return '';
    }
    const start = new Date(props.model.startMs);
    const end = new Date(props.model.endMs);
    const startTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    const endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
    const dayLabel = start.toLocaleDateString(locale.value, { day: '2-digit', month: 'short' });
    return `${startTime} – ${endTime} · ${dayLabel}`;
});

function statusLabel(status: ReservationStatus): string {
    return t(getReservationStatusLabel(status));
}

function statusSeverity(status: ReservationStatus): 'success' | 'info' | 'warn' | 'danger' {
    return STATUS_SEVERITY[status] ?? 'info';
}

// Escape dismissal, in both pointer modes. Without it a keyboard operator who opens the card by focusing an
// event has no way to close it, and the mouse path had no keyboard exit at all.
function onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
        emit('close');
    }
}

watch(
    () => props.open,
    (open) => {
        if (open) {
            document.addEventListener('keydown', onDocumentKeyDown);
        } else {
            document.removeEventListener('keydown', onDocumentKeyDown);
        }
    },
    { immediate: true }
);

function onDocumentPointerDown(event: PointerEvent) {
    const el = cardRef.value;
    if (el !== null && event.target instanceof Node && el.contains(event.target)) {
        return;
    }
    emit('close');
}

// Outside-tap dismissal exists on touch only — there is no move-the-pointer-away gesture there. Scroll dismissal
// already lives in the composable and is deliberately not duplicated.
watch(
    () => props.open && props.touchMode,
    (active) => {
        if (active) {
            document.addEventListener('pointerdown', onDocumentPointerDown);
        } else {
            document.removeEventListener('pointerdown', onDocumentPointerDown);
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown);
    document.removeEventListener('keydown', onDocumentKeyDown);
});
</script>

<template>
    <Teleport to="body">
        <Transition name="calendar-preview-fade">
            <div v-if="isVisible && model" :id="PREVIEW_CARD_ID" ref="cardRef" class="calendar-preview-card" :class="{ 'calendar-preview-card--maintenance': isMaintenance }" role="tooltip" :style="cardStyle" @pointerenter="emit('cardEnter')" @pointerleave="emit('cardLeave')">
                <div class="calendar-preview-card__rail"></div>

                <div class="calendar-preview-card__body">
                    <div class="calendar-preview-card__header">
                        <i class="calendar-preview-card__icon" :class="isMaintenance ? 'fa-solid fa-wrench' : 'fa-regular fa-calendar'" :aria-label="isMaintenance ? t('calendar.preview.maintenance') : undefined"></i>
                        <span class="calendar-preview-card__title">{{ displayTitle }}</span>
                        <Button v-if="touchMode" class="calendar-preview-card__close" icon="fa-solid fa-xmark" rounded text :aria-label="t('common.close')" @click="emit('close')" />
                    </div>

                    <div v-if="model.status !== null">
                        <Tag :value="statusLabel(model.status)" :severity="statusSeverity(model.status)" class="!text-xs" />
                    </div>

                    <div v-if="model.isPastUnconfirmed" class="calendar-preview-card__overdue">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        <span>{{ t('calendar.unconfirmedTooltip') }}</span>
                    </div>

                    <hr class="calendar-preview-card__divider" />

                    <div class="calendar-preview-card__range">{{ resourceRangeText }}</div>

                    <!-- D-18/IC-3. The operator's OWN clock, demoted beneath the space's. Sits immediately under the
                         primary range so all time information stays contiguous. Rendered only when the resource's
                         offset differs from the browser's at this event's own start instant; omitted entirely
                         otherwise (D-06), never an em dash, never empty.
                         The globe is aria-hidden on purpose: the visible label already carries the meaning, so an
                         announced icon would double it. (The retired chip marker needed a name because it had no
                         adjacent text.) -->
                    <div v-if="showBrowserLocalRange" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label"><i class="fa-solid fa-globe" aria-hidden="true"></i> {{ t('calendar.timezone.browserLocalTime') }}</span>
                        <span class="calendar-preview-card__value calendar-preview-card__value--time">{{ browserRangeText }}</span>
                    </div>

                    <div class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ t('calendar.preview.resource') }}</span>
                        <span class="calendar-preview-card__value">{{ model.resourceName }}</span>
                    </div>

                    <div v-if="model.otherResourceCount > 0" class="calendar-preview-card__count">{{ t('calendar.preview.otherResources', model.otherResourceCount, { count: model.otherResourceCount }) }}</div>

                    <!-- D-11. Sits where a reservation card's visitor / attendees / owner rows would be, all of which
                         are absent for a maintenance. Omitted entirely when null (D-06), never rendered as an em dash. -->
                    <div v-if="model.reducedCapacity != null" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ t('calendar.preview.reducedCapacity') }}</span>
                        <span class="calendar-preview-card__value">{{ model.reducedCapacity }}</span>
                    </div>

                    <div v-if="model.visitorName" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ t('calendar.preview.visitor') }}</span>
                        <span class="calendar-preview-card__value">{{ model.visitorName }}</span>
                    </div>

                    <div v-if="model.attendees > 0" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ t('calendar.preview.attendees') }}</span>
                        <span class="calendar-preview-card__value">{{ model.attendees }}</span>
                    </div>

                    <div v-if="model.ownerName" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ t('calendar.preview.owner') }}</span>
                        <span class="calendar-preview-card__value">{{ model.ownerName }}</span>
                    </div>

                    <hr v-if="model.description" class="calendar-preview-card__divider" />

                    <div v-if="model.description" class="calendar-preview-card__row">
                        <span class="calendar-preview-card__label">{{ excerptLabel }}</span>
                        <span class="calendar-preview-card__excerpt">{{ model.description }}</span>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>
